import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { captureEvent } from "@/lib/analyticsQueue";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCredits } from "@/contexts/CreditsContext";
import { useModels } from "@/hooks/useModels";
import { useGeneratedImages, type GeneratedImage } from "@/hooks/useGeneratedImages";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { useImageDelete } from "@/hooks/useImageDelete";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { apiPost } from "@/lib/api/apiClient";
import { uploadImagesToR2 } from "@/lib/api/uploadImage";
import { useImageUpload } from "@/hooks/useImageUpload";
import { downloadMedia, shareMedia } from "@/lib/mediaUtils";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";
import { captureGenerationError } from "@/lib/sentry";
import { INVALID_IMAGE_CODE } from "@shared/schemas/enums";
import { ASPECT_RATIOS } from "@/components/flowly/AspectRatioPicker/constants";
import {
  PROFILE_PHOTO_PRESETS,
  PROFILE_PHOTO_MAX_UPLOADS,
  PROFILE_PHOTO_MIN_UPLOADS,
  type ProfilePhotoGender,
  type ProfilePhotoPreset,
} from "@/config/profilePhotoPresets";
import { PROFILE_PHOTO_MODEL_SET } from "@shared/schemas/api";
import {
  PROFILE_PHOTO_CONFIG_ANCHOR_ID,
  PROFILE_PHOTO_PRESETS_ANCHOR_ID,
  PROFILE_PHOTO_PIPELINE,
} from "./constants";
import type { HairMode } from "@/components/flowly/ProfilePhotoHairPicker/ProfilePhotoHairPicker";
import type {
  GenerateProfilePhotoRequest,
  GenerateProfilePhotoResponse,
  ImageResolution,
} from "@shared/schemas/api";
import type { UploadedSource } from "./types";

const DEFAULT_ASPECT_RATIO = "3:4";
const DEFAULT_RESOLUTION = "1k";
// One image per model in the fixed set → the number of images per generation.
const PROFILE_PHOTO_IMAGE_COUNT = PROFILE_PHOTO_MODEL_SET.length;

let uploadIdCounter = 0;
const nextUploadId = () => `upload-${Date.now()}-${++uploadIdCounter}`;

export function useProfilePhoto() {
  const { t } = useLanguage();
  const { normalizeFilesOrWarn } = useImageUpload();
  const { refreshCredits, handleCreditError } = useCredits();
  const { models } = useModels("image");
  const {
    images: dbImages,
    pendingImages: dbPendingImages,
    completingIds: imageCompletingIds,
    failedImages,
    loading: imagesLoading,
  } = useGeneratedImages(undefined, PROFILE_PHOTO_PIPELINE);
  const { pendingCount, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({
      pipeline: PROFILE_PHOTO_PIPELINE,
      dbPendingCount: dbPendingImages.length,
      isDbLoading: imagesLoading,
    });
  const { deleteFailedImages, deleteImageByUrl } = useImageDelete();
  const [selectedMedia, setSelectedMedia] = useState<GeneratedImage | null>(null);

  const [gender, setGender] = useState<ProfilePhotoGender | null>(null);
  const [presetId, setPresetId] = useState<string | null>(null);
  // Custom-preset only: user-supplied background + clothing (both required),
  // edited inside the custom-style modal. Injected into the [BACKGROUND] /
  // [CLOTHING] prompt sections server-side.
  const [customBackground, setCustomBackground] = useState<string>("");
  const [customClothing, setCustomClothing] = useState<string>("");
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [uploads, setUploads] = useState<UploadedSource[]>([]);
  const [aspectRatio, setAspectRatio] = useState<string>(DEFAULT_ASPECT_RATIO);
  const [resolution, setResolution] = useState<string>(DEFAULT_RESOLUTION);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  // Snapshot of the last finished generation (source + result URLs) so the
  // hero keeps showing the user's before/after after we reset the upload form,
  // instead of snapping back to the default example images. In-memory only —
  // cleared on a new upload, gone on refresh (by design).
  const [lastShowcase, setLastShowcase] = useState<{ before: string; after: string } | null>(null);
  // Hair handling (step 4). Default = keep hair as in reference. Manual =
  // user typed an override that goes into the [HAIR] section of the prompt.
  const [hairMode, setHairMode] = useState<HairMode>("keep");
  const [customHair, setCustomHair] = useState<string>("");
  // Upload-guide popup ("shoot a clear, close-up, face-forward photo"). Shows
  // once after the first upload; `guideSeen` is persisted so it doesn't return
  // on refresh. No model picker anymore — one click generates one image per
  // model in PROFILE_PHOTO_MODEL_SET.
  const [showUploadGuide, setShowUploadGuide] = useState(false);
  const [guideSeen, setGuideSeen] = useState(false);
  const guideSeenRef = useRef(guideSeen);
  guideSeenRef.current = guideSeen;

  // --- Analytics: tool page view ---
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, { flow: FLOW_IDS.PROFILE_PHOTO });
  }, []);

  // We generate one image per model in PROFILE_PHOTO_MODEL_SET, so only offer
  // aspect ratios / resolutions that EVERY model in the set supports (the
  // server clamps per model, but we shouldn't surface an option one model
  // can't honor). Intersect their capability lists.
  const profilePhotoModels = useMemo(
    () =>
      PROFILE_PHOTO_MODEL_SET.map((id) => models.find((m) => m.id === id)).filter(
        (m): m is NonNullable<typeof m> => Boolean(m),
      ),
    [models],
  );
  const availableAspectRatios = useMemo(() => {
    const sets = profilePhotoModels
      .map((m) => m.capabilities.supportedAspectRatios)
      .filter((s): s is string[] => Boolean(s));
    if (sets.length === 0) return ASPECT_RATIOS;
    return ASPECT_RATIOS.filter((r) => sets.every((s) => s.includes(r.id)));
  }, [profilePhotoModels]);
  // Resolution is locked to 1k for this flow (flat flow price), so there is no
  // resolution picker — `resolution` stays DEFAULT_RESOLUTION throughout.

  const presetsForGender: ProfilePhotoPreset[] = useMemo(
    () => (gender ? PROFILE_PHOTO_PRESETS.filter((p) => p.gender === gender) : []),
    [gender],
  );

  const handleGenderChange = useCallback((next: ProfilePhotoGender) => {
    setGender(next);
    // Switching gender invalidates the previously chosen preset
    setPresetId(null);
    // Guide the user to the next step. rAF lets the preset grid mount first
    // so scrollIntoView can find its anchor.
    requestAnimationFrame(() => {
      document
        .getElementById(PROFILE_PHOTO_PRESETS_ANCHOR_ID)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const handlePresetSelect = useCallback((id: string) => {
    setPresetId(id);
    captureEvent(ANALYTICS_EVENTS.TEMPLATE_SELECTED, {
      flow: FLOW_IDS.PROFILE_PHOTO,
      preset_id: id,
    });
    const preset = PROFILE_PHOTO_PRESETS.find((p) => p.id === id);
    if (preset?.isCustom) {
      // Custom preset → open the "design your own" modal to collect the
      // required background + clothing.
      setIsCustomModalOpen(true);
    }
  }, []);

  const handleCustomBackgroundChange = useCallback((value: string) => {
    setCustomBackground(value);
  }, []);

  const handleCustomClothingChange = useCallback((value: string) => {
    setCustomClothing(value);
  }, []);

  // Continue from the modal → just close it. canGenerate enforces both fields,
  // and the page scrolls the user to the hairstyle step.
  const handleCustomModalContinue = useCallback(() => {
    setIsCustomModalOpen(false);
  }, []);

  // Closing the modal without both fields filled leaves an invalid custom
  // selection — deselect it so the user isn't stuck with a dead Create button.
  const handleCustomModalClose = useCallback(() => {
    setIsCustomModalOpen(false);
    if (!customBackground.trim() || !customClothing.trim()) {
      setPresetId(null);
    }
  }, [customBackground, customClothing]);

  /** Add files locally — instant blob preview, NO R2 upload yet. The actual
   *  R2 push is deferred until `generate()` runs (i.e. after the anonymous
   *  gate at the create step), so anonymous browsing costs us zero bandwidth
   *  and an anon user gets the exact same drag-and-drop UX as a paid one. */
  const handleAddFiles = useCallback(
    async (rawFiles: File[]) => {
      if (rawFiles.length === 0) return;
      const remainingSlots = PROFILE_PHOTO_MAX_UPLOADS - uploads.length;
      const rawAccepted = rawFiles.slice(0, remainingSlots);
      if (rawAccepted.length === 0) return;

      const accepted = await normalizeFilesOrWarn(rawAccepted);
      if (!accepted) return;

      captureEvent(ANALYTICS_EVENTS.IMAGE_UPLOAD, {
        flow: FLOW_IDS.PROFILE_PHOTO,
        quantity: accepted.length,
      });

      const wasEmpty = uploads.length === 0;
      const prevCount = uploads.length;
      const newSources: UploadedSource[] = accepted.map((file) => ({
        id: nextUploadId(),
        publicUrl: "",
        previewUrl: URL.createObjectURL(file),
        file,
      }));
      setUploads((prev) => [...prev, ...newSources]);
      // Source images changed → previously generated results no longer
      // reflect the current inputs, so drop them.
      setResults([]);
      setLastShowcase(null);
      if (wasEmpty && !guideSeenRef.current) {
        // Once after the first upload, pop the centered guide ("upload a clear,
        // close-up, face-forward photo"). Persisted via `guideSeen` so it
        // doesn't return on refresh.
        setShowUploadGuide(true);
        setGuideSeen(true);
      }
      // Only scroll to the config steps once we cross the minimum — below it the
      // config section is still hidden, so there'd be nothing to scroll to.
      const reachedMin =
        prevCount < PROFILE_PHOTO_MIN_UPLOADS &&
        prevCount + newSources.length >= PROFILE_PHOTO_MIN_UPLOADS;
      if (reachedMin) {
        requestAnimationFrame(() => {
          document
            .getElementById(PROFILE_PHOTO_CONFIG_ANCHOR_ID)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    },
    [uploads.length, normalizeFilesOrWarn],
  );

  const handleRemoveUpload = useCallback((id: string) => {
    setUploads((prev) => {
      const target = prev.find((u) => u.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((u) => u.id !== id);
    });
    // Source images changed → drop stale results so the hero "after" slot
    // returns to the placeholder / example instead of showing an outdated
    // generation tied to a no-longer-present source photo.
    setResults([]);
  }, []);

  const handleHairModeChange = useCallback((next: HairMode) => {
    setHairMode(next);
  }, []);

  const handleCustomHairChange = useCallback((value: string) => {
    setCustomHair(value);
  }, []);

  const handleClearFailed = useCallback(() => {
    if (failedImages.length > 0) {
      void deleteFailedImages(failedImages.map((f) => f.id));
    }
  }, [failedImages, deleteFailedImages]);

  // --- Lightbox callbacks ---

  const handleOpenLightbox = useCallback((img: GeneratedImage) => {
    setSelectedMedia(img);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setSelectedMedia(null);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!selectedMedia?.image_url) return;
    await downloadMedia(selectedMedia.image_url, "profile-photo", "png");
  }, [selectedMedia]);

  const handleShare = useCallback(async () => {
    if (!selectedMedia?.id) return;
    await shareMedia({ id: selectedMedia.id, type: "image" });
  }, [selectedMedia]);

  const handleDelete = useCallback(async () => {
    if (!selectedMedia?.image_url) return;
    const url = selectedMedia.image_url;
    setSelectedMedia(null);
    await deleteImageByUrl(url);
  }, [selectedMedia, deleteImageByUrl]);

  /** Navigation across the persisted gallery (cycles through dbImages). */
  const { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(dbImages, selectedMedia, setSelectedMedia);

  const isCustomPreset = useMemo(
    () => !!PROFILE_PHOTO_PRESETS.find((p) => p.id === presetId)?.isCustom,
    [presetId],
  );
  // Custom preset requires both fields filled before we can generate.
  const customFieldsReady =
    !isCustomPreset ||
    (customBackground.trim().length > 0 && customClothing.trim().length > 0);

  const canGenerate =
    !!gender &&
    !!presetId &&
    customFieldsReady &&
    uploads.length >= PROFILE_PHOTO_MIN_UPLOADS &&
    !isGenerating &&
    !isUploading;

  const generate = useCallback(async () => {
    if (!canGenerate || !gender || !presetId) return;

    setIsGenerating(true);
    setResults([]);
    // Optimistic: paint the pending cards now, before R2 upload / network.
    // The DB rows + Realtime update take over a beat later and the boost
    // auto-zeroes (usePersistentPendingBoost). Survives page refresh.
    incrementPending(PROFILE_PHOTO_IMAGE_COUNT);
    try {
      // Lazy R2 upload — every source still missing a publicUrl was kept as
      // a local File until now. We push only the missing ones (re-renders
      // skip already-uploaded uploads), then patch publicUrl back onto state
      // so a subsequent generate doesn't re-upload the same bytes.
      const needUpload = uploads.filter((u) => !u.publicUrl && u.file);
      if (needUpload.length > 0) {
        setIsUploading(true);
        try {
          const uploaded = await uploadImagesToR2(needUpload.map((u) => u.file!));
          setUploads((prev) =>
            prev.map((u) => {
              const idx = needUpload.findIndex((n) => n.id === u.id);
              return idx >= 0 ? { ...u, publicUrl: uploaded[idx].publicUrl } : u;
            }),
          );
          // Patch our local snapshot so the request body below uses the
          // fresh URLs without waiting for the setUploads commit.
          for (let i = 0; i < needUpload.length; i++) {
            needUpload[i].publicUrl = uploaded[i].publicUrl;
          }
        } finally {
          setIsUploading(false);
        }
      }

      const body: GenerateProfilePhotoRequest = {
        presetId,
        gender,
        sourceImageUrls: uploads.map((u) => u.publicUrl),
        aspectRatio,
        // Picker only yields 1k/2k/4k; the server Zod enum is the authoritative
        // guard that keeps the charged tier == the generated tier.
        resolution: resolution as ImageResolution,
        // One image per model — the server loops over this set.
        imageModelIds: [...PROFILE_PHOTO_MODEL_SET],
        ...(hairMode === "describe" && customHair.trim().length > 0
          ? { customHairstyle: customHair.trim() }
          : {}),
        // Custom preset → send the user's background + clothing (required;
        // canGenerate already enforced both are non-empty).
        ...(isCustomPreset
          ? {
              customBackground: customBackground.trim(),
              customClothing: customClothing.trim(),
            }
          : {}),
      };
      const response = await apiPost<GenerateProfilePhotoResponse>(
        "/api/generate-profile-photo",
        body,
      );
      setResults(response.images);
      // Pin the before/after to this finished run BEFORE we wipe the upload
      // form, so the hero keeps showing the user's source + result (both are
      // already-validated R2/result URLs — no re-upload, no validation bypass).
      if (uploads[0]?.publicUrl && response.images[0]) {
        setLastShowcase({ before: uploads[0].publicUrl, after: response.images[0] });
      }
      // Render landed — wipe the persisted draft AND reset the inputs to
      // defaults (you don't regenerate from the same photo). The result stays
      // visible via the DB-backed gallery (`dbImages`), untouched here.
      clearFlowState(FLOW_KEYS.PROFILE_PHOTO);
      uploads.forEach((u) => URL.revokeObjectURL(u.previewUrl));
      setUploads([]);
      setGender(null);
      setPresetId(null);
      setAspectRatio(DEFAULT_ASPECT_RATIO);
      setResolution(DEFAULT_RESOLUTION);
      setHairMode("keep");
      setCustomHair("");
      setCustomBackground("");
      setCustomClothing("");
    } catch (err) {
      // Failed before DB rows exist — remove the optimistic cards.
      decrementPending(PROFILE_PHOTO_IMAGE_COUNT);
      console.error("Error generating profile photo:", err);
      captureGenerationError(err, {
        flow: FLOW_IDS.PROFILE_PHOTO,
        action: "generate",
        model: PROFILE_PHOTO_MODEL_SET.join(","),
        endpoint: "/api/generate-profile-photo",
      });
      // Parity with old flows: insufficient credits → pricing modal;
      // refund-on-failure reflected via refreshCredits() in `finally`.
      if (!handleCreditError(err)) {
        // Upstream vision couldn't decode the user's photo — show a clear,
        // localized "try another image" toast rather than the raw upstream
        // error JSON. Matches the typed server code from
        // server/src/lib/llm/describeAppearance.ts.
        const code = (err as { code?: string })?.code;
        if (code === INVALID_IMAGE_CODE) {
          toast.error(t.profilePhoto.invalidImage);
        } else {
          toast.error(t.profilePhoto.generationFailed, {
            description:
              err instanceof Error ? err.message : t.profilePhoto.generationFailedDesc,
          });
        }
      }
    } finally {
      setIsGenerating(false);
      await refreshCredits();
    }
  }, [canGenerate, gender, presetId, uploads, aspectRatio, resolution, hairMode, customHair, isCustomPreset, customBackground, customClothing, refreshCredits, handleCreditError, incrementPending, decrementPending, t]);
  // Auth gate lives inside GenerateButton, so this is just a rename.
  const handleGenerate = generate;

  // Persist the user's selections so anonymous→signup doesn't reset them.
  // Persist config AND the uploaded photos across the auth/credit wall.
  // `uploads` is an image key → File bytes go to IndexedDB; rest is JSON.
  useFlowPersistence({
    key: FLOW_KEYS.PROFILE_PHOTO,
    state: { gender, presetId, aspectRatio, resolution, hairMode, customHair, customBackground, customClothing, guideSeen, uploads },
    imageKeys: ["uploads"],
    onRestore: (s) => {
      if (s.gender === "male" || s.gender === "female") setGender(s.gender);
      if (typeof s.presetId === "string") setPresetId(s.presetId);
      if (typeof s.aspectRatio === "string") setAspectRatio(s.aspectRatio);
      if (typeof s.resolution === "string") setResolution(s.resolution);
      if (s.hairMode === "describe" || s.hairMode === "keep") setHairMode(s.hairMode);
      if (typeof s.customHair === "string") setCustomHair(s.customHair);
      if (typeof s.customBackground === "string") setCustomBackground(s.customBackground);
      if (typeof s.customClothing === "string") setCustomClothing(s.customClothing);
      if (typeof s.guideSeen === "boolean") setGuideSeen(s.guideSeen);
      if (Array.isArray(s.uploads)) setUploads(s.uploads as UploadedSource[]);
    },
  });

  return {
    // State
    gender,
    presetId,
    uploads,
    aspectRatio,
    resolution,
    isUploading,
    isGenerating,
    results,
    lastShowcase,
    hairMode,
    customHair,
    isCustomPreset,
    customBackground,
    customClothing,
    isCustomModalOpen,
    presetsForGender,
    availableAspectRatios,
    models,

    // Upload-guide popup
    showUploadGuide,
    closeUploadGuide: () => setShowUploadGuide(false),

    // Persistent gallery (from DB via useGeneratedImages)
    dbImages,
    dbPendingImages,
    // max(optimistic boost, DB pending) — drives instant placeholder cards
    pendingCount,
    imageCompletingIds,
    failedImages,
    selectedMedia,

    // Handlers
    handleGenderChange,
    handlePresetSelect,
    handleAddFiles,
    handleRemoveUpload,
    handleHairModeChange,
    handleCustomHairChange,
    handleCustomBackgroundChange,
    handleCustomClothingChange,
    handleCustomModalContinue,
    handleCustomModalClose,
    setAspectRatio,
    setResolution,
    handleGenerate,
    handleClearFailed,
    handleOpenLightbox,
    handleCloseLightbox,
    handleDownload,
    handleShare,
    handleDelete,

    // Lightbox navigation
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,

    // Derived
    canGenerate,

    // i18n
    t,
  };
}
